import { datadogRum } from '@datadog/browser-rum';
import {
  Badge,
  Button,
  ButtonVariant,
  Icon,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import type { Document } from '~/entities/types';
import { useIsOverflowing } from '~/helpers/hook-utils/useIsOverflowing';
import { getDocReviewBadgeProps } from '../helpers/getReviewBadgeStatus';
import { DocumentRenderer } from './DocumentRenderer';

// ============================================================
//     TYPES
// ============================================================
interface Props {
  documents: Document[];
  activeDocToken: string;
  setActiveDocToken: (docToken: string) => void;
  /** Callback when the upload button is clicked. If not provided, the upload button will not be shown. */
  onUploadClick?: () => void;
  /** Upload popover element that wraps the Add button. If provided, this will wrap the AddButton. */
  uploadPopoverWrapper?: (button: React.ReactNode) => React.ReactNode;
}

// ============================================================
//     HELPERS
// ============================================================
const observabilityAction = (docToken: String) => {
  datadogRum.addAction('DocReviewV2Modal', {
    action: 'SWITCH_DOC',
    context: {
      docToken,
    },
  });
};

// ============================================================
//     STYLES
// ============================================================
const AddButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  height: 40,
  width: 40,
  flexShrink: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'var(--cl-gray-300)',
  color: 'var(--cl-gray-700)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'var(--cl-gray-400)',
  },
});

const DocNavigatorContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  gap: 'var(--cl-space-3)',
  justifyContent: 'flex-start',

  width: '100%',
  padding: 'var(--cl-space-2)',

  overflowX: 'auto',
  overscrollBehavior: 'contain',
  scrollSnapType: 'x mandatory',
  backgroundColor: 'var(--cl-gray-100)',
  scrollbarColor: 'var(--cl-gray-400) var(--cl-gray-100)',
});

// ============================================================
//     COMPONENT
// ============================================================
export const DocReviewNavigator: React.FC<Props> = ({
  activeDocToken,
  documents,
  onUploadClick,
  uploadPopoverWrapper,
  setActiveDocToken,
}) => {
  const navContainerRef = useRef<HTMLDivElement>(null);

  const {
    isOverflowingWidth: isNavOverflowing,
  } = useIsOverflowing(navContainerRef);

  const onScrollBtnClick = useCallback((direction: 'next' | 'prev') => {
    if (!navContainerRef.current) return;

    const dirValues = {
      next: 1,
      prev: -1,
    } satisfies Record<typeof direction, number>;

    const navContainerEl = navContainerRef.current;

    const { scrollLeft: currScrollLeft } = navContainerEl;

    navContainerEl.scroll({
      left: currScrollLeft + (80 * dirValues[direction]),
      behavior: 'smooth',
    });
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        ...(isNavOverflowing ? { paddingInline: 'var(--cl-space-10)' } : {}),
      }}
    >
      {isNavOverflowing && (
        <Button
          aria-label="Scroll to previous documents"
          onClick={() => { onScrollBtnClick('prev'); }}
          variant={ButtonVariant.tertiary}
          icon={IconName.ArrowLeft}
          style={{
            height: '100%',
            flexShrink: 0,
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            boxShadow: '2px 0px 6px oklch(0 0 0 / 0.08)',
            zIndex: 1,
          }}
        />
      )}
      <DocNavigatorContainer
        ref={navContainerRef}
        data-pendo-anchor="document-review-navigation"
      >
        {onUploadClick && (() => {
          const addButton = (
            <AddButton
              type="button"
              aria-label="Upload document"
              title="Upload document"
              onClick={onUploadClick}
            >
              <Icon icon={IconName.Add} size="large" />
            </AddButton>
          );
          return uploadPopoverWrapper ? uploadPopoverWrapper(addButton) : addButton;
        })()}
        {documents.map(doc => {
          const {
            approval_outcome: approvalOutcome,
            document_token: docToken,
            name,
          } = doc;

          const { iconName, status } = getDocReviewBadgeProps(approvalOutcome);

          const isActiveDoc = activeDocToken === docToken;

          return (
            <Tooltip key={docToken} content={name}>
              <Button
                type="button"
                onClick={() => {
                  observabilityAction(docToken);
                  setActiveDocToken(docToken);
                }}
                style={{
                  position: 'relative',
                  height: 80,
                  width: 80,
                  padding: 0,
                  flexShrink: 0,
                  scrollSnapAlign: 'start',
                  scrollMarginLeft: 'var(--cl-space-4)',
                  ...(isActiveDoc && {
                    boxShadow: [
                      '0px 0px 0px 1px var(--cl-juicyBlue-600)',
                      '0px 0px 0px 3px lch(from var(--cl-juicyBlue-600) l c h / .2)',
                    ].join(', '),
                  }),
                }}
              >
                <DocumentRenderer document={doc} previewMode />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 'var(--cl-space-1)',
                    right: 'var(--cl-space-1)',
                  }}
                >
                  <Badge status={status} icon={iconName} iconSize="small" />
                </div>
              </Button>
            </Tooltip>
          );
        })}
      </DocNavigatorContainer>
      {isNavOverflowing && (
        <Button
          aria-label="Scroll to next documents"
          variant={ButtonVariant.tertiary}
          onClick={() => { onScrollBtnClick('next'); }}
          icon={IconName.ArrowRight}
          style={{
            height: '100%',
            flexShrink: 0,
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            boxShadow: '-2px 0px 6px oklch(0 0 0 / 0.08)',
            zIndex: 1,
          }}
        />
      )}
    </div>
  );
};
